import {
  buildDefaultReferenceListEntries,
  mergeReferenceListEntries,
  resolveReferenceListLabel,
} from "./reference-list-entries.js";

function asStr(v) {
  return String(v ?? "").trim();
}

export const BILLING_CORRECTION_UI_REASON_IDS = Object.freeze([
  "participants_wrong",
  "attendance_wrong",
  "duration_wrong",
  "price_wrong",
  "other",
]);

const UI_REASON_TO_DB_REASON = Object.freeze({
  participants_wrong: "participant",
  attendance_wrong: "attendance",
  duration_wrong: "duration",
  price_wrong: "price",
  other: "other",
});

const LABEL_KEY_BY_ID = Object.freeze({
  participants_wrong: "trainerstunden.billingCorrection.draftReasonParticipants",
  attendance_wrong: "trainerstunden.billingCorrection.draftReasonAttendance",
  duration_wrong: "trainerstunden.billingCorrection.draftReasonDuration",
  price_wrong: "trainerstunden.billingCorrection.draftReasonPrice",
  other: "trainerstunden.billingCorrection.draftReasonOther",
});

const DB_REASON_TO_UI_REASON = Object.freeze({
  participant: "participants_wrong",
  attendance: "attendance_wrong",
  duration: "duration_wrong",
  price: "price_wrong",
  other: "other",
});

/**
 * @returns {import('./reference-list-entries.js').ReferenceListEntry[]}
 */
export function defaultBillingCorrectionReasonEntries() {
  return buildDefaultReferenceListEntries(BILLING_CORRECTION_UI_REASON_IDS);
}

/**
 * @param {unknown} lists
 */
export function resolveBillingCorrectionReasonEntries(lists) {
  const configured = lists && typeof lists === "object" ? lists.billingCorrectionReasons : null;
  return mergeReferenceListEntries(configured, defaultBillingCorrectionReasonEntries());
}

/**
 * @param {unknown} lists
 */
export function resolveBillingCorrectionReasonEntriesForAdmin(lists) {
  const configured = lists && typeof lists === "object" ? lists.billingCorrectionReasons : null;
  return mergeReferenceListEntries(configured, defaultBillingCorrectionReasonEntries(), { adminView: true });
}

/**
 * @param {{ id: string, label?: string }} entry
 * @param {(key: string) => string} t
 */
export function resolveBillingCorrectionReasonLabel(entry, t) {
  return resolveReferenceListLabel(entry, t, (id) => LABEL_KEY_BY_ID[id] || "");
}

/**
 * @param {unknown} lists
 * @returns {string[]}
 */
export function resolveBillingCorrectionReasonIds(lists) {
  return resolveBillingCorrectionReasonEntries(lists).map((row) => row.id);
}

/**
 * @param {string} uiReasonId
 */
export function mapBillingCorrectionUiReasonToDbCode(uiReasonId) {
  const id = asStr(uiReasonId);
  if (UI_REASON_TO_DB_REASON[id]) return UI_REASON_TO_DB_REASON[id];
  if (id.startsWith("correction_custom_")) return "other";
  return "";
}

/**
 * @param {string} dbReasonCode
 * @param {unknown} lists
 * @param {(key: string) => string} t
 */
export function resolveBillingCorrectionReasonLabelFromDbCode(dbReasonCode, lists, t) {
  const db = asStr(dbReasonCode);
  const uiId = DB_REASON_TO_UI_REASON[db] || "";
  if (!uiId) return db || "";
  const entry = resolveBillingCorrectionReasonEntries(lists).find((row) => row.id === uiId);
  if (entry) return resolveBillingCorrectionReasonLabel(entry, t);
  return typeof t === "function" ? t(LABEL_KEY_BY_ID[uiId] || "") : uiId;
}

/**
 * @param {string} uiReasonId
 * @param {unknown} lists
 */
export function isAllowedBillingCorrectionUiReasonId(uiReasonId, lists) {
  const id = asStr(uiReasonId);
  if (!id) return false;
  return resolveBillingCorrectionReasonIds(lists).includes(id);
}
